import React from "react";
import { Building2, MapPin, Crown, Users, Clock, Euro, Sun, Moon, Globe, ChevronRight } from "lucide-react";
import { eur, hours, fullName } from "@/components/etablissement/shared";

export function SiteCard({ site, seasonal, onOpen }) {
  const st = site.stats || {};
  return (
    <button onClick={() => onOpen(site)} data-testid={`site-card-${site.id}`}
            className={`text-left border-2 bg-white p-5 flex flex-col transition-all hover:-translate-y-0.5 hover:shadow-lg group ${site.active === false ? "border-[#DEE2E6] opacity-60" : site.is_siege ? "border-[#002FA7]" : "border-[#0A0A0A]"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className={`h-5 w-5 shrink-0 ${site.is_siege ? "text-[#002FA7]" : ""}`} />
          <div className="min-w-0">
            <div className="font-display font-black text-lg leading-tight truncate">{site.name}</div>
            <div className="font-mono text-[10px] text-[#868E96]">{site.code || "—"}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {site.is_siege && <span className="text-[9px] uppercase tracking-wider font-bold bg-[#002FA7] text-white px-1.5 py-0.5" data-testid={`site-siege-${site.id}`}>Siège</span>}
          {site.public && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold border border-[#DEE2E6] px-1.5 py-0.5 text-[#495057]"><Globe className="h-2.5 w-2.5" /> Public</span>}
          {site.active === false && <span className="text-[9px] uppercase tracking-wider font-bold bg-[#868E96] text-white px-1.5 py-0.5">Inactif</span>}
        </div>
      </div>

      {(site.adresse || site.ville) && (
        <div className="text-xs text-[#495057] flex items-start gap-1 mt-3">
          <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
          <span className="truncate">{[site.adresse, [site.code_postal, site.ville].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}</span>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 text-xs" data-testid={`site-card-resp-${site.id}`}>
        <Crown className={`h-3.5 w-3.5 ${st.responsable ? "text-[#FFD700]" : "text-[#DEE2E6]"}`} />
        {st.responsable ? <span className="font-semibold">{fullName(st.responsable)}</span> : <span className="text-[#868E96] italic">Aucun responsable</span>}
      </div>

      <div className="grid grid-cols-3 gap-px bg-[#DEE2E6] border border-[#DEE2E6] mt-4">
        <Cell icon={Users} label="Salariés" value={st.nb_salaries ?? 0} />
        <Cell icon={Clock} label="Heures" value={hours(st.heures_mois)} />
        <Cell icon={Euro} label="CA mois" value={eur(st.ca_mois)} />
      </div>

      <div className="mt-4 pt-3 border-t border-[#DEE2E6] flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-3">
          {seasonal && (
            <span className={`inline-flex items-center gap-1 font-bold uppercase tracking-wider ${st.open_today ? "text-[#059669]" : "text-[#E60000]"}`} data-testid={`site-open-${site.id}`}>
              {st.open_today ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />} {st.open_today ? "Ouvert" : "Fermé"}
            </span>
          )}
          {st.conges_pending > 0 && <span className="font-bold text-[#D97706]">{st.conges_pending} congé{st.conges_pending > 1 ? "s" : ""} à traiter</span>}
        </div>
        <span className="inline-flex items-center gap-1 font-bold uppercase tracking-wider text-[#002FA7] group-hover:gap-2 transition-all">Gérer <ChevronRight className="h-3 w-3" /></span>
      </div>
    </button>
  );
}

function Cell({ icon: Icon, label, value }) {
  return (
    <div className="bg-white p-2 text-center">
      <div className="flex items-center justify-center gap-1 text-[9px] uppercase tracking-wider text-[#868E96] font-bold"><Icon className="h-2.5 w-2.5" /> {label}</div>
      <div className="font-mono font-bold text-sm mt-0.5 truncate">{value}</div>
    </div>
  );
}
